/**
 * RTL (Right-to-Left) language utilities
 */

// List of RTL language codes
export const RTL_LANGUAGES = [
  'ar', // Arabic
  'he', // Hebrew
  'fa', // Persian/Farsi
  'ur', // Urdu
  'ku', // Kurdish
  'ps', // Pashto
  'sd', // Sindhi
  'yi', // Yiddish
  'arc', // Aramaic
  'dv', // Dhivehi/Maldivian
] as const

export type RTLLanguage = (typeof RTL_LANGUAGES)[number]

/**
 * Check if a language code represents an RTL language
 * @param languageCode - The language code to check (e.g., 'ar', 'en', 'ar-SA')
 * @returns true if the language is RTL, false otherwise
 */
export const isRTLLanguage = (languageCode: string): boolean => {
  // Extract the base language code (e.g., 'ar' from 'ar-SA')
  const baseLanguage = languageCode.split('-')[0].toLowerCase()
  return RTL_LANGUAGES.includes(baseLanguage as RTLLanguage)
}

/**
 * Get the text direction for a given language
 * @param languageCode - The language code
 * @returns 'rtl' for RTL languages, 'ltr' for LTR languages
 */
export const getTextDirection = (languageCode: string): 'rtl' | 'ltr' => {
  return isRTLLanguage(languageCode) ? 'rtl' : 'ltr'
}

/**
 * Update the HTML document direction attribute
 * @param languageCode - The current language code
 */
export const updateDocumentDirection = (languageCode: string): void => {
  const direction = getTextDirection(languageCode)
  document.documentElement.setAttribute('dir', direction)
  document.body.setAttribute('dir', direction)
}
